import { useContext, useEffect } from 'react';

import { Button, ButtonGroup, Heading, Modal, TextButton } from '@zillow/constellation-10';
import {
  useRentalManagerUserPropertySpecialOfferWebApiHandler_create,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_update,
} from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import actions from '../../../containers/CreateListingPage/actions';

import SpecialOfferFormField from '../../SpecialOfferFormField';
import { SPECIAL_OFFERS_ACTIONS } from '../../../constants/SpecialOffersConstants';

import clickstreamUtils from '../../../utils/clickstreamUtils';

import SpecialOffersCSEvents from '../../../model/ClickstreamEvent/SpecialOffersCSEvents';

import type { SpecialOfferProps } from '../../../model/SpecialOffer';

type Props = {
  action?: string;
  defaultOffer?: SpecialOfferProps;
  setShowSpecialOffersModal: (show: boolean) => void;
  isMultifamilyForm?: boolean;
  firstClickToAddOfferTrackingLocation?: string;
};

const SpecialOffersModal = ({
  action = SPECIAL_OFFERS_ACTIONS.ADD,
  defaultOffer = {
    description: '',
    startDateYyyyMmDd: '',
    endDateYyyyMmDd: '',
    offerIdEncoded: '',
  },
  setShowSpecialOffersModal,
  isMultifamilyForm = false,
  firstClickToAddOfferTrackingLocation = '',
}: Props) => {
  const {
    modularListingState: { isSaveDisabled },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty,
      currentProperty: { active, dateLastActivatedTimestamp },
    },
    user,
  } = useSelector((state) => state);

  const handleClose = () => {
    setShowSpecialOffersModal(false);
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isSavingSpecialOffers: false,
        isSaveDisabled: false,
      },
    });

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
    actions.removeServerError({
      reducerDispatch,
    });
  };

  const { isPending: isRMECreateLoading } = useRentalManagerUserPropertySpecialOfferWebApiHandler_create();
  const { isPending: isRMEUpdateLoading } = useRentalManagerUserPropertySpecialOfferWebApiHandler_update();

  useEffect(() => {
    // track when a user with no offers starts to add an offer
    if (firstClickToAddOfferTrackingLocation) {
      // get the suffix from current property info
      // track adding offer here
      const triggerLocationSuffix = clickstreamUtils.getListingStatusSuffix(active, dateLastActivatedTimestamp);
      SpecialOffersCSEvents.SpecialOffersAddNew({
        currentProperty,
        user,
        triggerLocation: `${firstClickToAddOfferTrackingLocation}${triggerLocationSuffix}`,
      });
    }
  }, []);

  const onSubmit = () => {
    // communicate that cta has been clicked
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isSavingSpecialOffers: true,
      },
    });
  };

  return (
    <Modal
      size={{ base: 'fullScreen', lg: 'md' }}
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      header={<Heading level={2}>{action === SPECIAL_OFFERS_ACTIONS.ADD ? 'Add' : 'Edit'} special offer</Heading>}
      body={
        <div
          data-testid="SpecialOffersModal"
          data-eventid={
            firstClickToAddOfferTrackingLocation ? SpecialOffersCSEvents.SpecialOffersAddNew.data_event_id : undefined
          }
        >
          <SpecialOfferFormField
            action={action}
            defaultOffer={defaultOffer}
            isMultifamilyForm={isMultifamilyForm}
            onClose={handleClose}
          />
        </div>
      }
      footer={
        <ButtonGroup aria-label="Special offer actions">
          <TextButton onClick={handleClose} disabled={isRMECreateLoading || isRMEUpdateLoading}>
            Cancel
          </TextButton>
          <Button
            emphasis="filled"
            onClick={onSubmit}
            loading={isRMECreateLoading || isRMEUpdateLoading}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default SpecialOffersModal;
